import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const BeritaListPage = () => {
  const navigate = useNavigate();
  // Remove token requirement - now public access
  const [berita, setBerita] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState('');
  const [allTags, setAllTags] = useState([]);

  useEffect(() => {
    fetchBerita();
    fetchTags();
  }, []);

  useEffect(() => {
    fetchBerita();
  }, [selectedTag]);

  const fetchBerita = async () => {
    setLoading(true);
    try {
      const params = selectedTag ? { tag: selectedTag } : {};
      const response = await axios.get(`${API}/berita`, { params });
      setBerita(response.data);
    } catch (error) {
      console.error('Error fetching berita:', error);
      toast.error('Gagal memuat berita');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/berita/tags`);
      setAllTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const truncateText = (markdownText, maxLength = 150) => {
    // Remove markdown syntax untuk preview
    let textContent = markdownText
      .replace(/#+\s+/g, '') // Remove headers
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold **text**
      .replace(/\*(.*?)\*/g, '$1') // Remove italic *text*
      .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove links [text](url)
      .replace(/!\[.*?\]\(.*?\)/g, '') // Remove images
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/`(.*?)`/g, '$1') // Remove inline code
      .replace(/^\s*[-*+]\s+/gm, '') // Remove bullet points
      .replace(/^\s*\d+\.\s+/gm, '') // Remove numbered lists
      .trim();
    
    if (textContent.length <= maxLength) return textContent;
    return textContent.substring(0, maxLength) + '...';
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white shadow-md py-3 sm:py-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              onClick={() => navigate('/')}
              className="text-ltb-bright hover:text-ltb-medium"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-800">Berita LTB+</h1>
          </div>
          <button
            onClick={() => navigate('/')}
            className="bg-ltb-bright text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-ltb-medium transition-all text-sm sm:text-base"
          >
            Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Filter Tags */}
        {allTags.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Filter by Tag</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedTag('')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedTag === '' 
                    ? 'bg-ltb-bright text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Semua
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedTag === tag 
                      ? 'bg-ltb-bright text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Berita List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-ltb-bright"></div>
            <p className="mt-2 text-gray-600">Memuat berita...</p>
          </div>
        ) : berita.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <p className="text-gray-500 text-lg">
              {selectedTag ? `Belum ada berita dengan tag "${selectedTag}"` : 'Belum ada berita tersedia'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {berita.map((item) => (
              <div 
                key={item.id}
                onClick={() => navigate(`/berita/${item.id}`)}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all cursor-pointer"
              >
                {/* Mobile & Desktop Responsive Layout */}
                <div className="flex flex-col sm:flex-row">
                  {/* Image */}
                  {item.gambar_url ? (
                    <div className="w-full sm:w-48 h-48 sm:h-32 flex-shrink-0">
                      <img 
                        src={item.gambar_url} 
                        alt={item.judul}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-full sm:w-48 h-48 sm:h-32 flex-shrink-0 bg-gray-200 flex items-center justify-center">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                      </svg>
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="flex-1 p-4 sm:p-6">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 hover:text-ltb-bright transition-colors line-clamp-2">
                      {item.judul}
                    </h2>
                    
                    <p className="text-sm sm:text-base text-gray-600 mb-3 line-clamp-2 sm:line-clamp-3">
                      {truncateText(item.isi_berita, 200)}
                    </p>
                    
                    {/* Date */}
                    <div className="flex items-center mb-2">
                      <span className="text-xs sm:text-sm text-gray-500">
                        📅 {formatDate(item.tanggal_publish)}
                      </span>
                    </div>
                    
                    {/* Tags */}
                    {item.t_berita_tags && item.t_berita_tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {item.t_berita_tags.slice(0, 3).map((tag) => (
                          <span 
                            key={tag.tag_name} 
                            className="bg-blue-100 text-blue-600 px-2 py-1 rounded text-xs font-medium"
                          >
                            #{tag.tag_name}
                          </span>
                        ))}
                        {item.t_berita_tags.length > 3 && (
                          <span className="text-xs text-gray-500 self-center">
                            +{item.t_berita_tags.length - 3} lagi
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Read More Link */}
                    <div className="flex justify-end">
                      <span className="text-xs sm:text-sm text-ltb-bright font-medium hover:text-ltb-medium">
                        Baca selengkapnya →
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BeritaListPage;